import { AxisCode } from '@atlas/types'
import { ArticleType } from '@/domain/article.types'
import type { ComposerSeed } from '@/lib/article-composer-link'
import { defaultPalettes, serializePalettes } from '@/lib/article-palette'
import { isEmptyRichText } from '@/lib/rich-text'
import { type ArticleFormState, EMPTY_FORM } from './form-model'

/**
 * The brand article template.
 *
 * Two sections, in order: `Brand voice` over free text, and `Brand colors` over a
 * palette block holding `Main palette` and `Secondary palette`.
 *
 * It is a STARTING POINT, not a schema. Once inserted, every heading, paragraph and
 * palette in it is ordinary editable content: the author may rename a heading,
 * delete a whole section, add a third palette, or reorder any of it, and the article
 * stays valid. Nothing re-applies the template and nothing validates against it.
 */

export const BRAND_VOICE_HEADING = 'Brand voice'
export const BRAND_COLORS_HEADING = 'Brand colors'

/** The template's body HTML — the same markup the editor's palette node produces. */
export function brandTemplateBody(): string {
	return [
		`<h1>${BRAND_VOICE_HEADING}</h1>`,
		'<p></p>',
		`<h1>${BRAND_COLORS_HEADING}</h1>`,
		serializePalettes(defaultPalettes()),
	].join('')
}

/**
 * Seed a form from the template.
 *
 * Sets the type to `brand_details` as well as the body, because the template exists
 * to produce the article a brand's page shows — a templated article typed as
 * `general` would be written for a surface that then refuses to display it. The
 * author may change the type afterwards.
 *
 * Anything the author already entered (a title, a classification tuple, imported
 * images) is preserved: seeding fills the body, it does not reset the form.
 */
export function applyBrandTemplate(form: ArticleFormState): ArticleFormState {
	return { ...form, content: brandTemplateBody(), articleType: ArticleType.BRAND_DETAILS }
}

/**
 * The form a fresh composer opens on, given the seed carried by the link that
 * opened it.
 *
 * Seeding is additive over the empty form: a brand arrives as a classification
 * tuple, the template fills the body and the type. A seed asking for neither
 * yields the ordinary blank composer.
 */
export function seededArticleForm(seed: ComposerSeed): ArticleFormState {
	const form: ArticleFormState = seed.brandCode
		? { ...EMPTY_FORM, classification: { [AxisCode.BRAND]: [seed.brandCode] } }
		: EMPTY_FORM
	return seed.brandTemplate ? applyBrandTemplate(form) : form
}

/**
 * Whether the body is still empty enough to seed over — used to decide whether
 * offering the template could overwrite authored prose.
 *
 * Delegates to the shared check so the composer's placeholder and this offer can
 * never disagree about what "empty" means: an editor showing a placeholder must be
 * one the template may still be applied to.
 */
export const isBodyEmpty = isEmptyRichText
